import { inject, Injectable, makeStateKey, TransferState } from '@angular/core';
import { Apollo, Query } from 'apollo-angular';
import { ApolloQueryResult } from '@apollo/client/core';
import { map, Observable, of, tap } from 'rxjs';
import { environment } from './../../../environments/environment';
import type { DynamoSidenavSection } from '@virginmediao2/dynamo/types';
import type { StrapiInterface, StrapiNavigation } from '@/shared/interfaces';
import { NAVIGATION_QUERY } from '@/shared/queries/navigation.query';
import { USER_LOGIN_QUERY } from '@/shared/queries/login.query';
import * as navigationsJson from '../../cms-json/navigations.json';
import { Utilities } from '../utilities/utilities';
import { FOOTER_QUERY } from '../queries/footer';

@Injectable({
  providedIn: 'root',
})
export class FooterGQL extends Query {
  override document = FOOTER_QUERY;
}

export interface StrapiNavigationWithType extends StrapiNavigation {
  type?: string;
}

export interface DynamoSidenavSectionWithType extends DynamoSidenavSection {
  type?: any;
}

@Injectable({
  providedIn: 'root',
})
export class StrapiCoreService {
  private apollo = inject(Apollo);
  private transferState = inject(TransferState);
  private footerGQL = inject(FooterGQL);

  public getNavigationContent(): Observable<DynamoSidenavSection> {
    const stateKey = makeStateKey<DynamoSidenavSection>(`strapi-legals-sidenav`);
    const state = this.transferState.get(stateKey, null);

    if (state && environment.transferState) {
      return of(state);
    }

    return this.apollo
      .query({
        query: NAVIGATION_QUERY,
        fetchPolicy: 'no-cache',
        variables: {
          uid: 'legals-sidenav',
        },
      })
      .pipe(
        map(({ data }: ApolloQueryResult<any>) => {
          const navigations = data.navigations[0] || navigationsJson;
          return this.navigationSectionAdapter(navigations);
        })
      )
      .pipe(tap((data) => this.transferState.set(stateKey, data)));
  }

  private navigationSectionAdapter = (data: StrapiNavigationWithType): DynamoSidenavSectionWithType => ({
    type: data.type || undefined,
    label: data.statement || undefined,
    // @ts-ignore
    icon: data.icon || undefined,
    href: data.externalUrl || undefined,
    children: (data.children || [])
      .slice()
      .sort((a, b) => ((b.displayOrder || 0) > (a.displayOrder || 0) ? -1 : 1))
      .map((child) => this.navigationSectionAdapter(child)),
  });

  private mapToStrapiNavigation = (navigation: StrapiNavigation) => {
    const strapiNavigation: any = {
      children: navigation,
    };
    return this.navigationSectionAdapter(strapiNavigation);
  };

  public getLoginContent(): Observable<any> {
    const stateKey = makeStateKey<any>(`UserLoginQuery`);
    const state = this.transferState.get(stateKey, null);

    if (state && environment.transferState) {
      return of(state);
    }
    return this.apollo
      .watchQuery({ query: USER_LOGIN_QUERY })
      .valueChanges.pipe(map((result: any) => result?.data?.userLogin))
      .pipe(tap((data) => this.transferState.set(stateKey, data)));
  }

  public getFooterContent(): Observable<StrapiInterface['footer']> {
    return this.footerGQL.fetch().pipe(map((result: ApolloQueryResult<any>) => Utilities.clone(result.data.footer)));
  }
}
